/**
 * useCompany Hook - React hook for company data, permissions, and member management
 *
 * Provides the current user's company data, their role within it,
 * and a hasPermission() helper that combines role + editor permissions + tier features.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useUser } from '@clerk/clerk-react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { waitForFirebaseAuth } from '../services/firebaseAuthBridge';
import { Company, EditorPermissions, Permission, UserRole } from '../types/company';
import { hasPermission as checkPermission, isSystemAdmin, isCompanyManager } from '../utils/permissions';
import { useTierFeatures } from './useTierFeatures';

interface UseCompanyReturn {
  company: Company | null;
  companyId: string | null;
  userRole: UserRole | null;
  editorPermissions: EditorPermissions | null;
  members: Company['members'];
  pendingRequests: Company['pendingRequests'];
  isManager: boolean;
  isEditor: boolean;
  isUser: boolean;
  isAdmin: boolean;
  needsOnboarding: boolean;
  hasPermission: (permission: Permission) => boolean;
  loading: boolean;
  error: string | null;
  refreshCompany: () => void;
}

export const useCompany = (): UseCompanyReturn => {
  const { user } = useUser();
  const { features: tierFeatures, loading: tierLoading } = useTierFeatures();

  const [company, setCompany] = useState<Company | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Listen to the user document for companyId and role changes
  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    // Wait for the Clerk->Firebase bridge sign-in before attaching the listener,
    // else the initial read races the sign-in and is denied under the rules —
    // which silently defaulted role to 'User' and hid company/admin features.
    let cancelled = false;
    let unsubUser = () => {};
    const userRef = doc(db, 'users', user.id);
    waitForFirebaseAuth().then(() => {
      if (cancelled) return;
      unsubUser = onSnapshot(
        userRef,
        (snap) => {
          if (snap.exists()) {
            const data = snap.data();
            setCompanyId(data.companyId || null);
            setUserRole((data.role as UserRole) || 'User');
            setNeedsOnboarding(data.needsOnboarding === true);
          } else {
            setCompanyId(null);
            setUserRole(null);
            setNeedsOnboarding(true);
          }
        },
        (err) => {
          console.error('Error listening to user document:', err);
          setError('Failed to load user data');
          setLoading(false);
        }
      );
    });

    return () => { cancelled = true; unsubUser(); };
  }, [user?.id, refreshKey]);

  // Listen to the company document when companyId is set
  useEffect(() => {
    if (!companyId) {
      setCompany(null);
      setLoading(false);
      return;
    }

    const companyRef = doc(db, 'companies', companyId);
    const unsubCompany = onSnapshot(
      companyRef,
      (snap) => {
        if (snap.exists()) {
          setCompany({ id: snap.id, ...snap.data() } as Company);
        } else {
          setCompany(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error listening to company document:', err);
        setError('Failed to load company data');
        setLoading(false);
      }
    );

    return () => unsubCompany();
  }, [companyId, refreshKey]);

  // Derived state
  const editorPermissions = company?.editorPermissions || null;
  const members = company?.members || [];
  const pendingRequests = company?.pendingRequests || [];

  const isAdmin = useMemo(() => isSystemAdmin(userRole), [userRole]);
  const isManager = useMemo(() => isCompanyManager(userRole), [userRole]);
  const isEditor = userRole === 'Editor';
  const isUser = userRole === 'User';

  // Permission checker that combines role + editor permissions + tier features
  const hasPermissionFn = useCallback(
    (permission: Permission): boolean => {
      return checkPermission(userRole, permission, editorPermissions, tierFeatures);
    },
    [userRole, editorPermissions, tierFeatures]
  );

  const refreshCompany = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  return {
    company,
    companyId,
    userRole,
    editorPermissions,
    members,
    pendingRequests,
    isManager,
    isEditor,
    isUser,
    isAdmin,
    needsOnboarding,
    hasPermission: hasPermissionFn,
    // Company/user data loading only — do not block on tier so Company Management can render for Owner/Admin
    loading,
    error,
    refreshCompany,
  };
};

export default useCompany;
